import React, { useState, useRef, useCallback } from 'react';
import Axios, { CancelToken, CancelTokenSource } from 'axios';
import * as Routes from '@/modules/routes';
import ReactDOM from 'react-dom';
import { useDebounceCallback } from 'usehooks-ts';
import DropdownMenu from 'components/common/Dropdown/DropdownMenu';
import { useDropdownEvents } from 'components/common/Dropdown';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import styles from '../../Content/SlideObjectText/AnchoredDropdown/AnchoredDropdown.module.scss';
import contentActionStyles from '../ContentActions.module.scss';
import { convertToHex } from '../../Utils';
import usePopover from '../../hooks/usePopover';
import { PopoverProps, PopoverData } from '../types';
import ColorPicker from '../EditBackgroundPopover/ColorPicker';
import BorderEditor from './BorderEditor';
import { initialWidth, initialColor, initialBorderRadius, initialOpacity } from './initialBorderValues';

const BorderPopover = ({ anchorId, openerClass, slideObject, styleDivRef }: PopoverProps) => {
  const cancelSourceRef = useRef<CancelTokenSource | null>(null);

  const divToStyle = () => {
    if (!styleDivRef) return null;

    return slideObject.modelType === 'Section'
      ? styleDivRef.current.getElementsByTagName('iframe')[0] : styleDivRef && styleDivRef.current;
  };

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [width, setWidth] = useState<string>(initialWidth(slideObject));
  const [color, setColor] = useState<string>(initialColor(slideObject));
  const [borderRadius, setBorderRadius] = useState<string>(initialBorderRadius(slideObject));
  const [opacity, setOpacity] = useState<string>(initialOpacity(slideObject));
  const updateSlideObjectInContext = useSlideShowContext(state => state.updateSlideObject);

  const ref = useRef<HTMLDivElement>();
  const dropdownEvents = useDropdownEvents(ref);

  const { coordinates, setIsUpdated } = usePopover({ anchorId, dropdownEvents, openerClass, ref });

  const updateSlideObject = (data: PopoverData, cancelToken: CancelToken) => {
    const params = {
      ...(data.color && { border_color: data.color }),
      ...(data.opacity && { border_opacity: convertToHex(data.opacity) }),
      ...(data.borderRadius && { border_radius: data.borderRadius }),
      ...(data.width && { border_width: data.width }),
      slideObjectId: slideObject.id,
    };

    return (
      Axios.patch(Routes.plato_api_slide_object_path(slideObject.id), params, { cancelToken })
        .then((res) => {
          updateSlideObjectInContext(res.data.slideObject);
        })
    );
  };

  const handleSubmit = async (data: PopoverData, closePopover = true): Promise<any> => {
    setIsUpdated(true);

    if (closePopover) dropdownEvents.setOpen(false);

    // Cancel any in-flight request
    if (cancelSourceRef.current) {
      cancelSourceRef.current.cancel();
    }

    // New cancel token source for this request
    const source = Axios.CancelToken.source();
    cancelSourceRef.current = source;

    try {
      return await updateSlideObject(data, source.token);
    } catch (err) {
      if (Axios.isCancel(err)) {
        // Request canceled
        return;
      }
      throw err;
    }
  };

  const debouncedSubmit = useCallback(useDebounceCallback(handleSubmit, 300), []);

  const handleChange = async (data: PopoverData) => {
    if (data.width) {
      divToStyle().classList.remove('!tw-border-0');
      setWidth(data.width);
      divToStyle().style.borderWidth = `${data.width}px`;
    }

    if (data.borderRadius) {
      setBorderRadius(data.borderRadius);
      divToStyle().style.borderRadius = `${data.borderRadius}px`;
    }

    if (data.color || data.opacity) {
      setColor(data.color);
      setOpacity(data.opacity);
      divToStyle().style.borderColor = `${data.color}${convertToHex(data.opacity)}`;
    }

    await debouncedSubmit(data, false);
  };

  return ReactDOM.createPortal(
    <DropdownMenu ref={ref} className={styles.margin} coordinates={coordinates} open={dropdownEvents.open}>
      {dropdownEvents.open && (
        <>
          <ColorPicker
            activeColor={color}
            activeOpacity={opacity}
            onChange={handleChange}
          />
          <BorderEditor
            activeBorderRadius={borderRadius}
            activeWidth={width}
            onChange={handleChange}
          />
          <div className={contentActionStyles.borderSubmitWrapper}>
            <button
              className={contentActionStyles.submitBtn}
              disabled={isSubmitting}
              type="button"
              onClick={() => {
                setIsSubmitting(true);
                handleSubmit({ color, opacity })
                  .then(() => {
                    setIsSubmitting(false);
                  });
              }}
            >
              OK
            </button>
          </div>
        </>
      )}
    </DropdownMenu>,
    document.body,
  );
};

export default BorderPopover;
